<template>
  <v-app id="app" class="neo-styled neo-styled-cloud">
    <alert :session="authenticated" />
    <confirm-dialog />

    <v-main
      v-if="sessionChecked"
      :has-system-message="showBar && showSystemMessage"
      :top-bar-hidden="metaConfigHideNavigation"
    >
      <div class="fixedTopbar">
        <system-message-viewer v-if="showBar && showSystemMessage" @close="forceCloseSystemMessage" />
        <app-top-bar v-if="showBar" @logout="signOut" />
      </div>

      <div class="scrollbarContainer" :session="authenticated" :key="contentKeyRefresher">
        <authenticated-parent v-if="authenticated" />
        <non-authenticated-parent v-else />
      </div>
    </v-main>

    <change-log-viewer v-if="authenticated" />
  </v-app>
</template>

<script lang="ts">
import { defineComponent, computed, reactive, onBeforeMount, ref } from "@vue/composition-api"
import { eventBus } from "@/main"
import { validateLicenseServer } from "@/utils/validate-license-server"
import { cacheInOrganization, recoverCachedOrganization } from "@/utils/organization-helper"
import { session, startSession } from "@/auth/session"
import { useStore } from "./composable/useStore"
import SystemMessageViewer from "@/components/systemMessageViewer/SystemMessageViewer.vue"
import ConfirmDialog from "@/components/confirmDialog/ConfirmDialogRoot.vue"
import Alert from "@/components/alert/AlertRoot.vue"
import AppFooter from "@/components/appFooter/AppFooterRoot.vue"
import AppTopBar from "@/components/appTopBar/TopBarRoot.vue"
import Router from "@/router"
import AuthenticatedParent from "@/views/auth/AuthenticatedParent.vue"
import NonAuthenticatedParent from "@/views/auth/NonAuthenticatedParent.vue"
import ChangeLogViewer from "@/components/changeLogViewer/ChangeLogViewer.vue"
import "@/styles/animations.scss"

export default defineComponent({
  name: "App",
  setup() {
    startSession()
    const { usersModule, adminModule } = useStore()
    const contentKeyRefresher = ref(0)
    const shouldForceCloseSystemMessage = ref(false)
    const router = reactive(Router)

    const authenticated = computed(() => {
      return session.auth.isAuthenticated
    })

    const sessionChecked = computed(() => {
      return session.auth.initiated
    })

    const showFeedbackSign = computed(() => {
      const hideFeedbackSign: boolean | null = Router.currentRoute.meta?.hideFeedbackSign
      return !hideFeedbackSign
    })

    const currentRoute = computed(() => {
      return router.currentRoute
    })

    const metaConfigHideNavigation = computed(() => {
      return currentRoute.value.meta?.hideNavigation
    })

    const showSystemMessage = computed(() => {
      const { message, active } = usersModule.systemMessage
      const { previewSystemMessage } = usersModule
      const showSystemMessage = sessionStorage.getItem("systemMessageShow") !== "false"
      const hasMessage = message?.length >= 2

      return (hasMessage && active && showSystemMessage && !shouldForceCloseSystemMessage.value) || previewSystemMessage
    })

    const showBar = computed(() => {
      const authenticatedRoute = currentRoute.value.meta?.requiresAuth

      if (!authenticatedRoute) {
        return false
      }

      return Boolean(usersModule.selfDetail?.id && authenticated.value)
    })
    const _refresh = () => {
      contentKeyRefresher.value++
    }

    const updateTitle = () => {
      const route = Router.currentRoute

      if (route.meta?.title) {
        document.title = `Viz Now - ${route.meta.title}`
      }
    }

    const sessionTrigger = async () => {
      if (await session.isValidUser()) {
        await usersModule.getMyDetails()
        const userDetail = usersModule.selfDetail

        if (userDetail) {
          await session.updateSelfDetails(userDetail)
          await validateLicenseServer()
          await usersModule.getSystemMessage()
        }
      }
    }

    const signOut = async () => {
      usersModule.clearCachedOrganization()
      session.clearCachedAccess()

      await session.terminate()
    }

    const changeOrganizationRefresh = async () => {
      await getUserDetails()
      await adminModule.getOrganizationDetail(usersModule.selectedOrganizationId)
      _refresh()
    }

    const forceCloseSystemMessage = () => {
      shouldForceCloseSystemMessage.value = true
    }

    const getUserDetails = async (): Promise<void> => {
      usersModule.updateOrganizationID()
      await session.validate()

      if (authenticated.value) {
        await validateLicenseServer()

        if (!recoverCachedOrganization()) {
          const user = await usersModule.getMyDetails()
          cacheInOrganization(user.organization.id)
          usersModule.updateOrganizationID()
        }
      }
    }

    eventBus.$on("validate:license", validateLicenseServer)
    eventBus.$on("update:organizationId", changeOrganizationRefresh)
    eventBus.$on("session:refreshed", sessionTrigger)

    onBeforeMount(sessionTrigger)
    onBeforeMount(updateTitle)

    return {
      forceCloseSystemMessage,
      showFeedbackSign,
      metaConfigHideNavigation,
      contentKeyRefresher,
      showSystemMessage,
      sessionChecked,
      authenticated,
      signOut,
      currentRoute,
      showBar,
      session
    }
  },
  components: {
    Alert,
    NonAuthenticatedParent,
    ConfirmDialog,
    AuthenticatedParent,
    ChangeLogViewer,
    SystemMessageViewer,
    AppTopBar,
    AppFooter
  }
})
</script>

<style lang="scss">
@import "@vizrt/vizrt.vuetify.neo/neo.scss";
@import "../node_modules/@vizrt/vizrticonfont/dist/vizrt-icon-font.css";
@import "@/styles/styles";
</style>
